import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Truck, ReceiptText, Home } from "lucide-react";
import { WA_LINK, WA_DISPLAY } from "@/lib/constants";

const LINES = [
  { text: "SERVICE AC, KULKAS", cls: "text-ink" },
  { text: "& MESIN LAUNDRY", cls: "text-ink" },
  { text: "JAKARTA.", cls: "text-brand-ink" },
];

const BADGES = [
  { icon: Truck, text: "Datang ke Lokasi" },
  { icon: ReceiptText, text: "Harga Transparan" },
  { icon: Home, text: "Rumah Tangga & Bisnis" },
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const imgY = useTransform(scrollY, [0, 900], [0, -90]);

  return (
    <section id="beranda" data-testid="hero-section" className="relative min-h-[92vh] lg:min-h-screen flex items-center overflow-hidden pt-20 lg:pt-24 pb-14 lg:pb-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/12687438/pexels-photo-12687438.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt=""
          className="h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/80 to-void" />
      </motion.div>
      <div className="absolute -top-32 -left-32 h-[500px] w-[500px] ice-glow -z-10" />

      <div className="max-w-7xl mx-auto w-full px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="hero-eyebrow"
            className="font-mono text-xs uppercase tracking-[0.35em] text-brand-ink mb-8"
          >
            Sanjaya Service — Jakarta & Sekitarnya
          </motion.p>

          <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[9vw] sm:text-5xl lg:text-6xl xl:text-[4rem]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block whitespace-nowrap ${line.cls}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            data-testid="hero-subtitle"
            className="mt-6 sm:mt-8 max-w-xl text-base md:text-lg text-ink/60 leading-relaxed"
          >
            <span className="sm:hidden">
              Service dan perbaikan untuk kebutuhan rumah tangga dan usaha di Jakarta dan sekitarnya.
            </span>
            <span className="hidden sm:inline">
              Sanjaya Service melayani service dan perbaikan AC, kulkas, mesin cuci, mesin laundry, mesin
              pengering laundry, dan showcase kaca dengan teknisi yang berpengalaman.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              aria-label="Pesan service Sanjaya Service via WhatsApp"
              className="group inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 font-display font-bold text-black text-base hover:bg-ink hover:text-white transition-colors duration-300"
            >
              <span className="sm:hidden">Chat WhatsApp</span>
              <span className="hidden sm:inline">Pesan Service via WhatsApp</span>
              <ArrowDown size={18} className="rotate-[-90deg] transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#layanan"
              data-testid="hero-services-link"
              className="font-mono text-xs uppercase tracking-[0.25em] text-ink/60 hover:text-brand-ink transition-colors duration-300 border-b border-ink/20 pb-1"
            >
              Lihat Layanan
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
          >
            {BADGES.map((b) => (
              <span key={b.text} className="inline-flex items-center gap-2 text-sm text-ink/50">
                <b.icon size={15} className="text-brand-ink" />
                {b.text}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block lg:col-span-5 relative"
        >
          <div className="absolute -inset-3 border border-brand/30 translate-x-6 translate-y-6" />
          <img
            src="https://images.pexels.com/photos/6471913/pexels-photo-6471913.jpeg?auto=compress&cs=tinysrgb&w=900"
            alt="Teknisi service AC Sanjaya Service Jakarta memeriksa unit AC"
            data-testid="hero-image"
            className="relative w-full aspect-[4/5] object-cover grayscale-[30%] contrast-110"
          />
          <div className="absolute -bottom-6 -left-6 bg-brand text-black px-6 py-4">
            <p className="font-display font-extrabold text-xl leading-none">{WA_DISPLAY}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] mt-1">Chat WhatsApp</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
